import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import {
  TranslationKey,
  CreateTranslationRequest,
  UpdateTranslationRequest,
  ApplicationType,
} from '../types/translation-types';
import {
  searchTranslationsLocally,
  paginateResults,
} from '../utils/translation-search-utils';

interface TranslationStore {
  // Data state
  translations: TranslationKey[];
  selectedTranslation: TranslationKey | null;
  totalCount: number;

  // Local-first search state
  allTranslations: TranslationKey[];
  isBackgroundLoading: boolean;
  isAllDataLoaded: boolean;
  isSearchingServer: boolean;
  searchSource: 'local' | 'server' | null;

  // Filter/pagination state
  selectedAppType: ApplicationType | null;
  searchQuery: string;
  currentPage: number;
  pageSize: number;

  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;

  // Error states
  error: string | null; // For fetch/load errors (shown in grid)
  submitError: string | null; // For create/update/delete errors (shown in blade)

  // Metadata
  lastUpdated: Date | null;

  // Actions
  fetchTranslations: () => Promise<void>;
  fetchAllTranslations: () => Promise<void>;
  searchLocally: (query: string) => void;
  searchServerFallback: (query: string) => Promise<void>;
  createTranslation: (data: CreateTranslationRequest) => Promise<TranslationKey>;
  updateTranslation: (id: number, data: UpdateTranslationRequest) => Promise<TranslationKey>;
  deleteTranslation: (id: number) => Promise<void>;
  setSelectedTranslation: (translation: TranslationKey | null) => void;
  setSelectedAppType: (appType: ApplicationType | null) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  clearError: () => void;
  clearSubmitError: () => void;
  reset: () => void;
}

const initialState = {
  translations: [],
  selectedTranslation: null,
  totalCount: 0,
  allTranslations: [],
  isBackgroundLoading: false,
  isAllDataLoaded: false,
  isSearchingServer: false,
  searchSource: null as 'local' | 'server' | null,
  selectedAppType: null as ApplicationType | null,
  searchQuery: '',
  currentPage: 0,
  pageSize: 50,
  isLoading: false,
  isSubmitting: false,
  error: null,
  submitError: null,
  lastUpdated: null,
};

// Incremented by every request/search that writes `translations`.
// A response whose generation is no longer current is discarded, so a slow
// unfiltered load can't overwrite newer search results.
let listRequestGen = 0;

export const useTranslationStore = create<TranslationStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchTranslations: async () => {
        const state = get();
        const gen = ++listRequestGen;

        // Don't fetch if no app type selected
        if (!state.selectedAppType) {
          set({ translations: [], totalCount: 0, isLoading: false, error: null });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const response = await services.getTranslationsUseCase.execute({
            applicationType: state.selectedAppType,
            skip: state.currentPage * state.pageSize,
            top: state.pageSize,
            filter: state.searchQuery || undefined,
            orderBy: 'keyName',
          });

          // Stale response - a newer request/search owns the list and loading flags
          if (gen !== listRequestGen) return;

          set({
            translations: response.value,
            totalCount: response['@odata.count'] ?? response.value.length,
            isLoading: false,
            isSearchingServer: false,
            lastUpdated: new Date(),
            error: null,
            searchSource: 'server',
          });
        } catch (error) {
          if (gen !== listRequestGen) return;
          console.error('TranslationStore.fetchTranslations: Failed:', error);
          set({
            isLoading: false,
            isSearchingServer: false,
            error: error instanceof Error ? error.message : 'Failed to load translations',
          });
        }
      },

      fetchAllTranslations: async () => {
        const state = get();

        // Guard: Don't fetch if already loading, no app type, or already loaded
        if (state.isBackgroundLoading || !state.selectedAppType || state.isAllDataLoaded) {
          return;
        }

        set({ isBackgroundLoading: true });

        try {
          const services = servicesContainer.getServices();
          const response = await services.getAllTranslationsUseCase.execute(state.selectedAppType);

          // App type changed while loading - this cache belongs to the old app type
          if (get().selectedAppType !== state.selectedAppType) return;

          set({
            allTranslations: response.value,
            isAllDataLoaded: true,
            isBackgroundLoading: false,
          });

          // If the user searched while the cache was loading, re-apply it on the full data set.
          // Not via searchLocally - that resets the page and can fire a server fallback.
          const { searchQuery, currentPage, pageSize } = get();
          if (searchQuery.trim()) {
            const { filteredItems, totalMatches } = searchTranslationsLocally(response.value, searchQuery);
            // No local match: keep the current server results rather than issuing another request
            if (filteredItems.length > 0) {
              ++listRequestGen; // Local results win over any in-flight server fetch
              const lastPage = Math.max(0, Math.ceil(totalMatches / pageSize) - 1);
              const page = Math.min(currentPage, lastPage);
              set({
                translations: paginateResults(filteredItems, page, pageSize),
                totalCount: totalMatches,
                currentPage: page,
                searchSource: 'local',
                isLoading: false,
                isSearchingServer: false,
              });
            }
          }
        } catch (error) {
          console.error('TranslationStore.fetchAllTranslations: Failed:', error);
          // Fail silently - user can still use server search
          set({ isBackgroundLoading: false });
        }
      },

      searchLocally: (query: string) => {
        const state = get();
        ++listRequestGen; // Invalidate any in-flight server response

        set({ searchQuery: query, currentPage: 0 });

        // If no local data, fall back to server.
        // When the page was reset from N to 0, the page-change effect in Translations.tsx already fetches.
        if (!state.isAllDataLoaded) {
          if (state.currentPage === 0) get().fetchTranslations();
          return;
        }

        // Perform local search
        const { filteredItems, totalMatches } = searchTranslationsLocally(
          state.allTranslations,
          query
        );

        // Check if we need server fallback
        if (query.trim() && filteredItems.length === 0) {
          // The gen bump above discarded any in-flight fetch, so its isLoading would never be cleared
          set({ isLoading: false });
          get().searchServerFallback(query);
          return;
        }

        // Paginate and display
        const paginatedResults = paginateResults(filteredItems, 0, state.pageSize);

        set({
          translations: paginatedResults,
          totalCount: totalMatches,
          searchSource: 'local',
          isLoading: false,
          isSearchingServer: false,
        });
      },

      searchServerFallback: async (query: string) => {
        const state = get();

        if (!state.selectedAppType) return;

        const gen = ++listRequestGen;
        set({ isSearchingServer: true });

        try {
          const services = servicesContainer.getServices();
          const response = await services.getTranslationsUseCase.execute({
            applicationType: state.selectedAppType,
            skip: 0,
            top: state.pageSize,
            filter: query,
            orderBy: 'keyName',
          });

          if (gen !== listRequestGen) return;

          set({
            translations: response.value,
            totalCount: response['@odata.count'] ?? response.value.length,
            isLoading: false,
            isSearchingServer: false,
            searchSource: 'server',
          });
        } catch (error) {
          if (gen !== listRequestGen) return;
          console.error('TranslationStore.searchServerFallback: Failed:', error);
          set({
            isLoading: false,
            isSearchingServer: false,
            error: 'Search failed. Please try again.',
          });
        }
      },

      createTranslation: async (data: CreateTranslationRequest) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          const newTranslation = await services.createTranslationUseCase.execute(data);

          // Update allTranslations cache if loaded
          const state = get();
          if (state.isAllDataLoaded) {
            set({
              allTranslations: [...state.allTranslations, newTranslation],
            });
          }

          // Refresh the visible list
          await get().fetchTranslations();

          set({ isSubmitting: false, submitError: null });
          return newTranslation;
        } catch (error) {
          console.error('TranslationStore.createTranslation: Failed:', error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to create translation',
          });
          throw error;
        }
      },

      updateTranslation: async (id: number, data: UpdateTranslationRequest) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          const updatedTranslation = await services.updateTranslationUseCase.execute(id, data);

          // In-place update: Merge request data with existing translation
          set((state) => {
            const existingTranslation = state.translations.find((t) => t.translationKeyId === id);
            if (!existingTranslation) {
              return {
                isSubmitting: false,
                submitError: null,
                lastUpdated: new Date(),
              };
            }

            // Build updated values from request data (what we sent is what we expect)
            let finalValues = existingTranslation.values;
            if (data.values) {
              const updatedLangCodes = new Set(data.values.map((v) => v.languageCode));
              const preservedValues = existingTranslation.values.filter(
                (v) => !updatedLangCodes.has(v.languageCode)
              );
              const newValues = data.values.map((v) => {
                const existing = existingTranslation.values.find((ev) => ev.languageCode === v.languageCode);
                // Always spread from existing to preserve required fields (translationValueId, updated)
                // If no existing, create placeholder values (will be corrected on next fetch)
                return {
                  translationValueId: existing?.translationValueId ?? 0,
                  languageCode: v.languageCode,
                  value: v.value,
                  updated: existing?.updated ?? new Date().toISOString(),
                  updatedByName: existing?.updatedByName,
                };
              });
              finalValues = [...preservedValues, ...newValues];
            }

            const mergedTranslation: TranslationKey = {
              ...existingTranslation,
              // Update description if provided in request
              description: data.description !== undefined ? data.description : existingTranslation.description,
              values: finalValues,
            };

            // Also update allTranslations cache if loaded
            const updatedAllTranslations = state.isAllDataLoaded
              ? state.allTranslations.map((t) =>
                  t.translationKeyId === id ? mergedTranslation : t
                )
              : state.allTranslations;

            return {
              translations: state.translations.map((t) =>
                t.translationKeyId === id ? mergedTranslation : t
              ),
              allTranslations: updatedAllTranslations,
              isSubmitting: false,
              selectedTranslation: mergedTranslation,
              submitError: null,
              lastUpdated: new Date(),
            };
          });

          return updatedTranslation;
        } catch (error) {
          console.error(`TranslationStore.updateTranslation(${id}): Failed:`, error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to update translation',
          });
          throw error;
        }
      },

      deleteTranslation: async (id: number) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          await services.deleteTranslationUseCase.execute(id);

          // Update allTranslations cache if loaded
          const state = get();
          if (state.isAllDataLoaded) {
            set({
              allTranslations: state.allTranslations.filter((t) => t.translationKeyId !== id),
            });
          }

          // Refresh the visible list
          await get().fetchTranslations();

          // Clear selected if it was the deleted one
          const currentState = get();
          if (currentState.selectedTranslation?.translationKeyId === id) {
            set({ selectedTranslation: null });
          }

          set({ isSubmitting: false, submitError: null });
        } catch (error) {
          console.error(`TranslationStore.deleteTranslation(${id}): Failed:`, error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to delete translation',
          });
          throw error;
        }
      },

      setSelectedTranslation: (translation: TranslationKey | null) => {
        set({ selectedTranslation: translation });
      },

      setSelectedAppType: (appType: ApplicationType | null) => {
        // Clear cache when app type changes
        set({
          selectedAppType: appType,
          currentPage: 0,
          allTranslations: [],
          isAllDataLoaded: false,
          isBackgroundLoading: false,
          searchSource: null,
        });
      },

      setSearchQuery: (query: string) => {
        set({ searchQuery: query, currentPage: 0 });
      },

      setCurrentPage: (page: number) => {
        const state = get();

        set({ currentPage: page });

        // If using local search, re-paginate from cache
        if (state.isAllDataLoaded && state.searchSource === 'local') {
          const { filteredItems } = searchTranslationsLocally(
            state.allTranslations,
            state.searchQuery
          );
          const paginatedResults = paginateResults(filteredItems, page, state.pageSize);
          set({ translations: paginatedResults });
        }
        // Server-based pagination: the currentPage effect in Translations.tsx fetches the new page
      },

      clearError: () => set({ error: null }),

      clearSubmitError: () => set({ submitError: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'translation-storage',
      partialize: (state) => ({
        // Only persist app type preference
        selectedAppType: state.selectedAppType,
      }),
    }
  )
);
